import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  isDrawer?: boolean;
  /** No X in the header, for a confirmation whose footer already has the way out (Leave without saving?). */
  hideClose?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  isDrawer = false,
  hideClose = false,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const content = isDrawer ? (
    <>
      <div className="backdrop show" onClick={onClose} />
      <aside className="drawer show" role="dialog" aria-modal="true">
        <div className="drawer-head">
          <div>
            {title && <div className="drawer-title">{title}</div>}
            {subtitle && <div className="drawer-sub">{subtitle}</div>}
          </div>
          {/* Icon-only: needs a name for screen readers. Sized on the icon itself, since
              `.bk-icon-btn` only sizes its svg on pages that load bookings.css. */}
          {!hideClose && (
            <button type="button" className="bk-icon-btn" onClick={onClose} aria-label="Close">
              <X size={18} aria-hidden />
            </button>
          )}
        </div>
        <div className="drawer-body">{children}</div>
        {footer && <div className="drawer-foot">{footer}</div>}
      </aside>
    </>
  ) : (
    <div className="modal-backdrop show" onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className="modal">
        <div className="modal-head">
          <div>
            {title && <div className="mt">{title}</div>}
            {subtitle && <div className="ms">{subtitle}</div>}
          </div>
          {/* Icon-only: needs a name for screen readers. Sized on the icon itself, since
              `.bk-icon-btn` only sizes its svg on pages that load bookings.css. */}
          {!hideClose && (
            <button type="button" className="bk-icon-btn" onClick={onClose} aria-label="Close">
              <X size={18} aria-hidden />
            </button>
          )}
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};
